// File: src/context/ActingAsContext.jsx
//
// Global "Acting as" state for admin / ServiceProviderAdmin roles.
//
// - selectedInstructor: null = "Myself" (default)
// - Persists to localStorage('actingAsInstructorId') so api.js's apiRequest
//   can read it synchronously and attach the X-Acting-As header.
// - Resets to "Myself" on logout (call clearActingAs from your logout handler).

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'actingAsInstructorId';
const NAME_STORAGE_KEY = 'actingAsInstructorName';

const ActingAsContext = createContext(null);

export const ActingAsProvider = ({ children }) => {
  const [selectedInstructor, setSelectedInstructor] = useState(() => {
    const id   = localStorage.getItem(STORAGE_KEY);
    const name = localStorage.getItem(NAME_STORAGE_KEY);
    return id ? { instructorId: id, instructorName: name || id } : null;
  });

  // Keep localStorage in sync — apiRequest reads this directly
  useEffect(() => {
    if (selectedInstructor) {
      localStorage.setItem(STORAGE_KEY, selectedInstructor.instructorId);
      localStorage.setItem(NAME_STORAGE_KEY, selectedInstructor.instructorName || '');
    } else {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(NAME_STORAGE_KEY);
    }
  }, [selectedInstructor]);

  /**
   * Select an instructor to act as. Pass null to switch back to "Myself".
   * @param {{ instructorId: string, instructorName: string } | null} instructor
   */
  const setActingAs = useCallback((instructor) => {
    setSelectedInstructor(instructor);
  }, []);

  const clearActingAs = useCallback(() => {
    setSelectedInstructor(null);
  }, []);

  const isActingAsSelf = selectedInstructor === null;

  return (
    <ActingAsContext.Provider value={{
      selectedInstructor,
      isActingAsSelf,
      setActingAs,
      clearActingAs
    }}>
      {children}
    </ActingAsContext.Provider>
  );
};

/**
 * Hook to access/set the "Acting as" state.
 * @returns {{
 *   selectedInstructor: {instructorId, instructorName} | null,
 *   isActingAsSelf: boolean,
 *   setActingAs: (instructor: {instructorId, instructorName} | null) => void,
 *   clearActingAs: () => void
 * }}
 */
export const useActingAs = () => {
  const ctx = useContext(ActingAsContext);
  if (!ctx) throw new Error('useActingAs must be used within an ActingAsProvider');
  return ctx;
};
